import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { TURNSTILE_SITE_KEY } from '../constants/envVars';

/**
 * Holds the Turnstile token for one protected form.
 *
 * Spread `turnstileProps` onto a <TurnstileWidget />, gate the submit on
 * `ensureTurnstileToken()`, and call `resetTurnstile()` whenever the request
 * comes back rejected. Tokens are single use, so a spent one has to be traded
 * for a fresh challenge before the visitor can try again.
 *
 * With no site key configured the widget renders nothing and
 * `ensureTurnstileToken()` always passes, which is what keeps local development
 * working without a Cloudflare account.
 */
function useTurnstile() {
  const { t } = useTranslation();
  const [token, setToken] = useState('');
  const [resetSignal, setResetSignal] = useState(0);

  const clearToken = useCallback(() => setToken(''), []);

  const resetTurnstile = useCallback(() => {
    setToken('');
    setResetSignal((signal) => signal + 1);
  }, []);

  // Guards the submit while the challenge is still running, so the visitor gets
  // told to wait instead of collecting a 422 from the backend.
  const ensureTurnstileToken = useCallback(() => {
    if (TURNSTILE_SITE_KEY && !token) {
      toast.error(t('captcha.pending'));

      return false;
    }

    return true;
  }, [token, t]);

  return {
    turnstileToken: token,
    ensureTurnstileToken,
    resetTurnstile,
    turnstileProps: {
      onVerify: setToken,
      onExpire: clearToken,
      resetSignal,
    },
  };
}

export default useTurnstile;
